<script setup>
import {ref} from "vue";
import {useRouter} from "vue-router";
import {definePage} from "unplugin-vue-router/runtime";
import {useAuthenticationStore} from '@/stores/authentication.js';
import createApiService from "@/services/api.js";
import ClientTask from "@/components/client/ClientTask.vue";
import {useMessageStore} from "@/stores/message.js";
import {useAdminStore} from "@/stores/admin.js";

definePage({
  meta: {
    title: 'Home',
    requiresAuth: true,
    layout: 'client',
  },
})

const loading = ref(true);
const adminStore = useAdminStore();
const authStore = useAuthenticationStore();
const router = useRouter();
const api = createApiService(router);

const activeTasks = ref([]);
const completedTasks = ref([]);

const errors = ref([]);
const message = ref(null);

const getTasks = async () => {
  errors.value = [];

  let userId = adminStore.getClient !== null ? adminStore.getClient : authStore.getCurrentUserId;

  try {
    const response = await api.post('getclienttasks', {userId: userId});

    if (response.status === 200) {
      activeTasks.value = response.data.data.activeTasks;
      completedTasks.value = response.data.data.completedTasks;
    }
    else {
      errors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    console.log(e)
    errors.value = e.response?.status === 400 ? [e.response.data.message] : ['Er is iets misgegaan. Probeer het later nog eens.'];
  } finally {
    loading.value = false;
  }
}

const today = new Date();
const hours = today.getHours();
const dagdeel = computed(() => {
  if (hours >= 6 && hours < 12) {
    return 'ochtend';
  }
  else if (hours >= 12 && hours < 18) {
    return 'middag';
  }
  else if (hours >= 18 && hours < 24) {
    return 'avond';
  }
  else {
    return 'nacht';
  }
})

onMounted(async () => {
  await getTasks();

  const messageStore = useMessageStore();
  if (messageStore.message !== '') {
    message.value = {
      message: messageStore.message,
      type: messageStore.type,
    }
    messageStore.removeMessage();
  }
})

</script>

<template>
  <v-alert
    v-if="message"
    class="mx-auto mb-5"
    max-width="500"
    :color="message.type"
    variant="elevated"
  >
    {{ message.message }}
  </v-alert>

  <v-alert
    v-if="errors.length > 0"
    class="mx-auto"
    max-width="500"
    color="error"
    variant="elevated"
  >
    <template #text>
            <span
              v-for="(error, index) in errors"
              :key="index"
            >
              {{ error }}
            </span>
    </template>
  </v-alert>

  <the-loader v-if="loading" class="mx-auto my-10"/>
  <template v-else>

    <h1 class="text-center pt-5">Beginscherm</h1>

    <v-sheet
      v-if="activeTasks.length > 0"
      class="bg-primary text-center pa-6 text-start-desktop"
    >
      <h3 class="mb-6">Openstaande taken {{dagdeel}}:</h3>
      <v-row class="ga-4 tasks-flex-start" justify="center">
        <client-task
          v-for="taskStatus in activeTasks"
          :key="taskStatus.id"
          :id="taskStatus.id"
          :task="taskStatus.task"
        ></client-task>
      </v-row>
    </v-sheet>
    <v-sheet class="bg-primary text-center pa-6" v-else>
      <h3 class="text-center my-6">Geen actieve taken gevonden</h3>
    </v-sheet>

    <hr
      v-if="activeTasks.length > 0 && completedTasks.length > 0"
      class="w-100" id="task-spacer"
    >

    <v-sheet
      v-if="completedTasks.length > 0"
      class="bg-primary text-center pa-6 text-start-desktop"
    >
      <h3 class="mb-6">Afgeronde taken:</h3>
      <v-row class="ga-4 tasks-flex-start" justify="center">
        <client-task
          v-for="taskStatus in completedTasks"
          :key="taskStatus.id"
          :id="taskStatus.id"
          :task="taskStatus.task"
          finished
        ></client-task>
      </v-row>
    </v-sheet>
  </template>
</template>
